import { selectCurrentHotelId } from 'accessors'
import { SecondaryRegularButton } from 'components/ReusableComponents/Button/RegularButtons'
import { SecondarySmallButton } from 'components/ReusableComponents/Button/SmallButtons'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import Table, {
  Cell,
  Row,
  TitleCell,
  TitleRow
} from 'components/ReusableComponents/Table'
import BookingHistory from 'features/BookingDetails/BookingHistory'
import BookingStatusText from 'features/BookingDetails/BookingText'
import BookingDetailsDialog from 'features/BookingDetailsDialog'
import ViewMessageIcon from 'features/CustomCampaigns/CampaignResults/ViewMessageIcon'
import moment from 'moment'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import campaignApi from 'services/api/campaign'
import config from 'services/config'
import { formatPrice } from 'utils/common'
import downloadAsCsv from 'utils/downloadAsCsv'

import styles from './CampaignResults.module.scss'
import { ReactComponent as ExcelIcon } from './excel.svg'

const MessagePreview = ({ mediaUrl, text, currentSubsidiaryId }) => {
  const [isVisible, setIsVisible] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const buttonRef = useRef(null)
  const previewRef = useRef(null)

  const calculatePosition = useCallback(() => {
    if (!buttonRef.current) return

    const buttonRect = buttonRef.current.getBoundingClientRect()
    const previewHeight = 300
    const previewWidth = 290
    const padding = 10

    let top = buttonRect.top - previewHeight / 2 + buttonRect.height / 2
    let left = buttonRect.right + padding

    if (top < padding) {
      top = padding
    }

    if (top + previewHeight > window.innerHeight - padding) {
      top = window.innerHeight - previewHeight - padding
    }

    if (left + previewWidth > window.innerWidth - padding) {
      left = buttonRect.left - previewWidth - padding
    }

    setPosition({ top, left })
  }, [])

  const handleMouseEnter = () => {
    calculatePosition()
    setIsVisible(true)
  }

  const handleMouseLeave = () => {
    setIsVisible(false)
  }

  return (
    <div className={styles.previewHolder}>
      <button
        ref={buttonRef}
        className={styles.previewButton}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <ViewMessageIcon />
      </button>
      {isVisible && (
        <div
          ref={previewRef}
          className={styles.preview}
          style={{
            top: position.top,
            left: position.left
          }}
        >
          {mediaUrl && (
            <div
              className={styles.textMedia}
              style={{
                backgroundImage: `url(${
                  config.inviteMediaBaseUrl
                }/${currentSubsidiaryId}/${encodeURIComponent(mediaUrl)})`
              }}
            />
          )}
          <div className={styles.bubble}>
            <p>{text}</p>
            <span className={styles.bubbleTail}></span>
          </div>
        </div>
      )}
    </div>
  )
}

function getDateRange(date1, date2) {
  if (moment(date1).format('MMM') === moment(date2).format('MMM')) {
    return `${moment(date1).format('MMM')} ${moment(date1).format('D')} - ${moment(
      date2
    ).format('D')}`
  } else {
    return `${moment(date1).format('MMM D')} - ${moment(date2).format('MMM D')}`
  }
}

const analyticCardColors = {
  ceruleanBlue: '#2B6CB0',
  cornFlowerBlue: '#667EEA',
  mediumPurple: '#9B51E0',
  coolBlue: '#288DA4',
  goldenGrass: '#E2A62C',
  ocenGreen: '#3AAF71'
}

const CampaignResults = ({ id, onClose }) => {
  const [isLoading, setIsLoading] = useState(true)
  const [noBookings, setNoBookings] = useState(false)

  const [activeSubscriberId, setActiveSubscriberId] = useState()
  const [activeBookingId, setActiveBookingId] = useState()

  const [isErred, setIsErred] = useState()

  const [stats, setStats] = useState({})
  const [bookings, setBookings] = useState([])

  const [sortIndex, setSortIndex] = useState(5)
  const [sortAscending, setSortAscending] = useState(true)

  function handleTitleClick(i) {
    const sortableIndexes = [0, 1, 2, 3, 4, 5, 6, 7]

    if (sortIndex === i) {
      setSortAscending(d => !d)
    }

    if (sortableIndexes.includes(i)) {
      setSortIndex(i)
    }
  }

  function handleClickDownload() {
    const titleRow = [
      'Guest name',
      'Days since last visit',
      'Miles to hotel',
      'Last visit date',
      'Number of bookings in last 12 months',
      'Invited on',
      'Days between invite and booking',
      'Booked on',
      'Check-in date',
      'Days between booking and check-in',
      'Check-out date',
      'Rate code',
      'Total rate',
      'Booking status',
      'Confirmation code',
      'Medium'
    ]

    const resAsArrays = bookings.map(b => [
      b.fullName,
      b.bookedDaysAfterLastBooking,
      b.distanceToHotel ? Number(b.distanceToHotel).toFixed(2) : 0,
      b.lastStayDate === '0001-01-01T00:00:00'
        ? 'Cancelled'
        : moment(b.lastStayDate).format('YYYY/MM/DD'),
      b.numberOfBookingsLast12Month,
      moment(b.sentOn).format('YYYY/MM/DD'),
      b.bookingDaysAfterText,
      moment(b.bookingDate).format('YYYY/MM/DD'),
      moment(b.checkIn).format('YYYY/MM/DD'),
      b.bookDaysBeforeCheckIn,
      moment(b.checkOut).format('YYYY/MM/DD'),
      b.rateCode,
      b.totalRate ? Number(b.totalRate).toFixed(2) : 0,
      b.bookingStatus,
      b.confirmationCode,
      b.medium
    ])

    const dataArray = [titleRow, ...resAsArrays]
    const fileName = `campaign_results.csv`

    downloadAsCsv(dataArray, fileName)
  }

  function sortResults(results) {
    const sortedData = results.sort((a, b) => {
      switch (sortIndex) {
        case 0:
          if (a.fullName === b.fullName) return 0

          return a?.fullName?.toUpperCase() > b?.fullName?.toUpperCase() ? 1 : -1

        case 1:
          return a.daysSinceLastStay - b.daysSinceLastStay

        case 2:
          return a.distanceToHotel - b.distanceToHotel

        case 3:
          return new Date(a.lastStayDate) - new Date(b.lastStayDate)

        case 4:
          return new Date(a.sentOn) - new Date(b.sentOn)

        case 5:
          return new Date(a.bookingDate) - new Date(b.bookingDate)

        case 6:
          return new Date(a.checkIn) - new Date(b.checkIn)

        case 7:
          return a.rateCode - b.rateCode

        case 8:
          return Number(a.totalRate) - Number(b.totalRate)

        case 9:
          return a.bookingStatus - b.bookingStatus

        default:
          return 0
      }
    })

    return sortAscending ? sortedData : [...sortedData].reverse()
  }

  const currentSubsidiaryId = useSelector(selectCurrentHotelId)

  useEffect(() => {
    campaignApi
      .getCampaignResults({ campaignId: id })
      .then(res => {
        if (res === null) {
          setNoBookings(true)
          return
        }

        const { deliveriesTotal, deliveriesSent, clicksTotal, bookingsTotal } = res

        setStats({ deliveriesTotal, deliveriesSent, clicksTotal, bookingsTotal })

        setBookings(res.campaignBookingViews)
      })
      .catch(err => {
        console.warn(err)
        setIsErred(true)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [id])

  if (noBookings) {
    return (
      <ConfirmationDialog
        title="Campaign results"
        maxWidth={1300}
        contentPadding={0}
        onClose={onClose}
        onConfirm={onClose}
        hasConfirmButton={true}
        allowOverflow={true}
      >
        <p className={styles.noResultsText}>
          There are no bookings for this drip campaign yet. Please check back later.
        </p>
      </ConfirmationDialog>
    )
  }

  if (activeSubscriberId) {
    return (
      <BookingHistory
        subscriberId={activeSubscriberId}
        onClose={() => setActiveSubscriberId(null)}
      />
    )
  }

  if (activeBookingId) {
    return (
      <BookingDetailsDialog
        bookingId={activeBookingId}
        onClose={() => setActiveBookingId(null)}
      />
    )
  }

  return (
    <ConfirmationDialog
      title="Campaign results"
      maxWidth={1300}
      contentPadding={0}
      onClose={onClose}
      hasConfirmButton={false}
      allowOverflow={false}
    >
      {noBookings && <p>There are no bookings for this drip campaign yet.</p>}
      {isErred && <p>Something went wrong while loading results:</p>}
      {isLoading && !isErred && <LoadingIndicator inline />}
      {!isLoading && !isErred && (
        <>
          <div className={styles.funnelWrapper}>
            <div className={styles.labels}>
              <div
                className={styles.label}
                style={{ backgroundColor: analyticCardColors.ceruleanBlue }}
              >
                <p className={styles.labelValue}>{stats.deliveriesTotal}</p> audience size
              </div>
              <div
                className={styles.label}
                style={{ backgroundColor: analyticCardColors.cornFlowerBlue }}
              >
                <p className={styles.labelValue}>{stats.deliveriesSent}</p>
                sent messages
              </div>
              <div
                className={styles.label}
                style={{ backgroundColor: analyticCardColors.mediumPurple }}
              >
                <p className={styles.labelValue}>{stats.clicksTotal}</p>clicks
              </div>
              <div
                className={styles.label}
                style={{ backgroundColor: analyticCardColors.ocenGreen }}
              >
                <p className={styles.labelValue}>{stats.bookingsTotal}</p>bookings
              </div>
            </div>
          </div>
          <div className={styles.bookingsWrapper}>
            <div className={styles.titleHolder}>
              <h2 className={styles.bookingsTitle}>
                {stats.bookingsTotal} bookings - Total rate{' '}
                {formatPrice(bookings.reduce((prev, curr) => prev + curr.totalRate, 0))}
              </h2>
              <SecondaryRegularButton
                className={styles.copyToExcelButton}
                onClick={() => handleClickDownload()}
              >
                <ExcelIcon /> Download as CSV
              </SecondaryRegularButton>
            </div>

            <div className={styles.tableHolder}>
              <Table
                widths={[140, 80, 60, 90, 65, 100, 80, 100, 80, 75, 100, 50]}
                sortIndex={sortIndex}
                sortDirection={sortAscending ? 'up' : 'down'}
                handleTitleCellClick={handleTitleClick}
              >
                <TitleRow className={styles.titleRow}>
                  <TitleCell>Guest name</TitleCell>
                  <TitleCell>
                    Days since <br /> last stay
                  </TitleCell>
                  <TitleCell>
                    Miles <br /> to hotel
                  </TitleCell>
                  <TitleCell>Last stay</TitleCell>
                  <TitleCell>Medium</TitleCell>
                  <TitleCell>Invited on</TitleCell>
                  <TitleCell>Booked on</TitleCell>
                  <TitleCell>Stay dates</TitleCell>
                  <TitleCell>Rate code</TitleCell>
                  <TitleCell>Total rate</TitleCell>
                  <TitleCell>Booking status</TitleCell>
                  <TitleCell>Text</TitleCell>
                  <TitleCell></TitleCell>
                </TitleRow>
                {sortResults(bookings).map(b => (
                  <Row className={styles.row} key={b.bookingId}>
                    <Cell className={styles.fullNameRow}>
                      {b.subscriptionId ? (
                        <button
                          className={styles.fullNameButton}
                          onClick={() => setActiveSubscriberId(b.subscriptionId)}
                        >
                          {b.fullName}
                        </button>
                      ) : (
                        b.fullName
                      )}
                    </Cell>
                    <Cell>{b.daysSinceLastStay || '-'}</Cell>
                    <Cell>{b.distanceToHotel || '-'}</Cell>
                    <Cell>
                      {b.lastStayDate === '0001-01-01T00:00:00'
                        ? 'Cancelled'
                        : moment(b.lastStayDate).format('MMM D, YYYY')}
                    </Cell>
                    <Cell>{b.medium}</Cell>
                    <Cell>
                      {b.sentOn === '0001-01-01T00:00:00'
                        ? '-'
                        : moment(b.sentOn).format('MMM D, YYYY')}
                    </Cell>
                    <Cell>{moment(b.bookingDate).format('MMM D')}</Cell>
                    <Cell>{getDateRange(b.checkIn, b.checkOut)}</Cell>
                    <Cell>{b.rateCode}</Cell>
                    <Cell>{formatPrice(b.totalRate)}</Cell>
                    <Cell>
                      <BookingStatusText
                        status={b.bookingStatus}
                        bookingDate={b.bookingDate}
                      />
                    </Cell>
                    <Cell>
                      {b.medium !== 'Email' && (
                        <MessagePreview
                          mediaUrl={b.mediaUrl}
                          text={b.text}
                          currentSubsidiaryId={currentSubsidiaryId}
                        />
                      )}
                    </Cell>
                    <Cell>
                      <SecondarySmallButton
                        className={styles.viewButton}
                        onClick={() => setActiveBookingId(b.bookingId)}
                        disabled={!b.bookingId}
                      >
                        {' '}
                        Details
                      </SecondarySmallButton>
                    </Cell>
                  </Row>
                ))}
              </Table>
            </div>
          </div>
        </>
      )}
    </ConfirmationDialog>
  )
}

export default CampaignResults
